import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  TextField,
  Typography,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Slider,
  useMediaQuery,
  Popover,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  Alert,
  Link as MuiLink,
  Chip,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";

import PendingActionsIcon from "@mui/icons-material/PendingActions";
import RateReviewIcon from "@mui/icons-material/RateReview";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import API_BASE_URL from "../../utils/api";
import CloseIcon from "@mui/icons-material/Close";
import FloatingInstructions from "../../components/FloatingInstructions/FloatingInstructions";

import Joyride, { CallBackProps } from "react-joyride";
import { Rnd } from "react-rnd";
const CAMPAIGN_TIPS = [
  {
    id: "filters",
    title: "Image filters can help",
    body: [
      "Some pages in this document are very light, dark, or blurry. Use the image filters to make the text easier to read.",
      "",
      "You can:",
      "• Increase brightness to bring out faint ink",
      "• Increase contrast to sharpen letters",
      "• Use invert to flip light-on-dark text",
      "",
      "Try small adjustments until the handwriting feels comfortable to read.",
    ].join("\n"),
  },
  {
    id: "handwriting",
    title: "Historical handwriting",
    body: [
      "Many documents use older handwriting styles.",
      "",
      "Tips:",

      "• Read a word in context; nearby words often help you guess unclear letters.",
      "• If you really cannot read a word, mark it with [?] instead of guessing.",
      "",
      "Over time, your eye will adjust and you will recognize common shapes more easily.",
    ].join("\n"),
  },
  {
    id: "need-help",
    title: "Need more help?",
    body: [
      "You can always come back to the full How-To Guide while you work.",
      "",
      "Good habits:",
      "• Save your work as you go.",
      "• If a page feels too hard, save and move to another—someone else can continue.",
      "",
      "You do not have to finish every page you open. Small contributions still help.",
    ].join("\n"),
  },
];

const HOW_TO_TOPICS = [
  {
    id: "getting-started",
    title: "Getting started",
    body: [
      "1. Choose a page to work on.",
      "2. Read the page image carefully from top to bottom.",
      "3. Type what you see into the transcription box.",
      "",
      "General flow:",
      "• Transcribe: one volunteer types the page.",
      "• Review: another volunteer checks for accuracy.",
      "• Complete: once accepted, the transcription is ready for use in search and research.",
    ].join("\n"),
  },
  {
    id: "basic-rules",
    title: "Basic rules",
    body: [
      "Text order:",
      "• Transcribe text in the order it appears on the page.",
      "• If you are unsure, transcribe it in the order that makes sense if read aloud.",
      "",
      "Spelling, grammar, and punctuation:",
      "• Preserve original spelling, grammar, and punctuation.",
      "• Do not modernize or paraphrase.",

      "",
      "Line and page breaks:",
      "• Preserve line breaks unless a single word is split.",
      "• If a word is split across lines or pages, type the whole word where it begins.",
      "• If a page is blank, type 'Nothing to transcribe' and save.",
    ].join("\n"),
  },
  {
    id: "things-to-avoid",
    title: "Things to avoid",
    body: [
      "Avoid these while transcribing:",
      "",
      "• Do not add notes or comments into the transcription text.",
      "• Do not expand abbreviations—type exactly what you see.",
      "• Do not try to recreate layout (columns, fancy spacing, etc.).",
      "• Do not describe images or decorations in the transcription box.",
      "",
    ].join("\n"),
  },
  {
    id: "print-and-images",
    title: "Printed text and images",
    body: [
      "Printed or typed text:",
      "• Still needs to be transcribed so it becomes searchable.",
      "• 'Transcribe with OCR' may help, but you should always correct the result.",
      "",
      "Catalog numbers, headings, and letterhead:",
      "• Transcribe page numbers, catalog numbers, and letterheads—they often contain names, places, and dates.",
      "",
      "Images only:",
      "• If a page has only images and no text, type 'Nothing to transcribe'.",
    ].join("\n"),
  },
  {
    id: "unusual-text",
    title: "Unusual text and symbols",
    body: [
      "Illegible or unclear text:",
      "• If you truly cannot read a word, use [?].",
      "• If you can read part of a word, type those letters and use question marks for the rest, e.g. M[????].",
    ].join("\n"),
  },
  {
    id: "review",
    title: "How to review",
    body: [
      "Review is the final quality check before a page is marked complete.",
      "",
      "When reviewing:",
      "• Read the entire transcription while looking at the page.",
      "• Go line-by-line; preserved line breaks make this easier.",
      "• Check for missing words, typos, and deviations from the rules.",
      "",
      "Actions:",
      "• Accept: only if the transcription is complete and accurate.",
      "• Edit: if you need to correct or complete text. Save and submit so another reviewer can check again.",
      "",
      "You should not review pages you originally transcribed yourself.",
    ].join("\n"),
  },
];
const getStatusMeta = (status, contributorsCount = 0) => {
  if (!status) return null;

  const base = `Registered Contributors: ${contributorsCount}`;

  switch (status.toLowerCase()) {
    case "in progress":
      return {
        icon: <PendingActionsIcon fontSize="small" color="info" />,
        title: "In progress",
        contributorsLine: base,
        message: "Someone started this transcription. Can you finish it?",
        color: "info",
      };

    case "in review":
    case "needs review":
      return {
        icon: <RateReviewIcon fontSize="small" color="warning" />,
        title: "Needs review",
        contributorsLine: base,
        message: "Check this transcription thoroughly. Accept if correct!",
        color: "warning",
      };

    case "not started":
      return {
        icon: <NoteAddIcon fontSize="small" color="default" />,
        title: "Not started",
        contributorsLine: "",
        message: "Transcribe this page.",
        color: "default",
      };

    case "completed":
      return {
        icon: <CheckCircleIcon fontSize="small" color="success" />,
        title: "Completed",
        contributorsLine: base,
        message: "This transcription is finished! You can read.",
        color: "success",
      };

    default:
      return null;
  }
};

const PageEditor = () => {
  const { documentId, pageNumber } = useParams();
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [transcription, setTranscription] = useState("");
  const [message, setMessage] = useState("");
  const [totalPages, setTotalPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(parseInt(pageNumber));
  const [isEditing, setIsEditing] = useState(false);
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogAction, setdialogAction] = useState(null);
  const [successDialog, setSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [invert, setInvert] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const normalizedRotation = ((rotation % 360) + 360) % 360;
  const isQuarterTurn = normalizedRotation === 90 || normalizedRotation === 270;

  const [userImage, setUserImage] = useState([]);
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const [hasStartedTypingTimer, setHasStartedTypingTimer] = useState(false);

  const [filtersAnchor, setFiltersAnchor] = useState(null);
  const isMobile = useMediaQuery("(max-width:768px)");
  const [tipsOpen, setTipsOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [activeTipIndex, setActiveTipIndex] = useState(0);
  const [activeTopicId, setActiveTopicId] = useState(HOW_TO_TOPICS[0].id);
  const [showTopicList, setShowTopicList] = useState(true);
  const imgRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const prevPageRef = useRef(null);
  const lastLockedPageRef = useRef(null);

  const [ocrResult, setOcrResult] = useState("");
  const [imageForCrop, setImageForCrop] = useState(null);

  const [selectArea, setSelectArea] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const imageContainerRef = useRef(null);
  const [imageContainerSize, setImageContainerSize] = useState({
    width: 0,
    height: 0,
  });
  const [pageOpenedAt, setPageOpenedAt] = useState(null);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [warningDialogMode, setWarningDialogMode] = useState(null); // "submit" | "accept"
  const [warningDialogMessage, setWarningDialogMessage] = useState("");
  const [ocrError, setOcrError] = useState("");
  const [ocrInfo, setOcrInfo] = useState("");
  const [ocrCooldownUntil, setOcrCooldownUntil] = useState(null);
  const [ocrSecondsLeft, setOcrSecondsLeft] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  const [favoriteLoading, setFavoriteLoading] = useState(false);
  useEffect(() => {
    const loadFavorite = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) return;

        const res = await axios.get(
          `${API_BASE_URL}/user-activity/favorite/${documentId}/${pageNumber}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setIsFavorite(res.data.isFavorite === true);
      } catch (err) {
        console.error("Failed to load favorite:", err);
      }
    };

    loadFavorite();
  }, [documentId, pageNumber]);
  const steps = [
    {
      target: "#joy-select-area",
      content: "First, toggle this checkbox to begin selecting an area.",
      placement: "right",
      disableBeacon: false,
    },
    {
      target: "#joy-crop-box",
      content:
        "Great! Now drag or resize this dashed box to cover the text you want to OCR.",
      placement: "top",
      spotlightPadding: 10,
      disableBeacon: true,
    },
    {
      target: "#joy-extract-btn",
      content: "Finally, click here to extract the text from your selection.",
      placement: "top",
      disableBeacon: true,
    },
  ];
  const [runTour, setRunTour] = useState(false);
  const [copySucess, setCopySuccess] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [cropBox, setCropBox] = useState({
    x: 30,
    y: 10,
    width: 300,
    height: 400,
  });
  const ocrResultRef = useRef(null);
  const [removedImages, setRemovedImages] = useState([]);
  const [transcriptionSessionStartedAt, setTranscriptionSessionStartedAt] =
    useState(null);
  const [reviewSessionStartedAt, setReviewSessionStartedAt] = useState(null);
  const reviewIdleTimerRef = useRef(null);

  const [reviewIdleDialogOpen, setReviewIdleDialogOpen] = useState(false);

  const [reviewTimerPaused, setReviewTimerPaused] = useState(false);
  const REVIEW_IDLE_TIMEOUT = 10 * 1000;
  const contributorsCount = pageData?.contributors?.length || 0;
  const statusMeta = getStatusMeta(pageData?.status, contributorsCount);

  const activeTopic =
    HOW_TO_TOPICS.find((t) => t.id === activeTopicId) || HOW_TO_TOPICS[0];
  const handleOpenDialog = (action) => {
    setdialogAction(action);
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSuccessClose = () => {
    setSuccessDialog(false);
  };
  const getMinSecondsForAction = (action, contentType = "unknown") => {
    if (action === "submit") {
      return contentType === "handwritten" ? 12 : 8;
    }

    if (action === "accept") {
      return contentType === "handwritten" ? 10 : 6;
    }

    return 0;
  };

  const getElapsedSeconds = () => {
    if (!pageOpenedAt) return null;
    return Math.max(
      0,
      Math.round((Date.now() - new Date(pageOpenedAt).getTime()) / 1000)
    );
  };
  const shouldWarnTooFast = (action) => {
    const contentType = pageData?.contentType || "unknown";
    const minSeconds = getMinSecondsForAction(action, contentType);

    const elapsed =
      action === "submit"
        ? getTranscriptionElapsedSeconds()
        : getReviewElapsedSeconds();

    if (elapsed == null) return false;
    return elapsed < minSeconds;
  };

  const openTooFastWarning = (action) => {
    const contentType = pageData?.contentType || "unknown";
    const minSeconds = getMinSecondsForAction(action, contentType);

    const elapsed =
      action === "submit"
        ? getTranscriptionElapsedSeconds()
        : getReviewElapsedSeconds();

    if (action === "submit") {
      setWarningDialogMessage(
        `This page is being submitted very quickly (${elapsed ?? 0} seconds). Please take a little more time to review the transcription before sending it for review, especially if OCR was used. Recommended minimum for this ${contentType} page: ${minSeconds} seconds.`
      );
    } else {
      setWarningDialogMessage(
        `This review is being completed very quickly (${elapsed ?? 0} seconds). Please compare the page image and transcription carefully before marking it complete. Recommended minimum for this ${contentType} page: ${minSeconds} seconds.`
      );
    }

    setWarningDialogMode(action);
    setWarningDialogOpen(true);
  };

  const handleSave = async () => {
    handleOpenDialog("save");
  };
  const handleSubmitForReview = async () => {
    handleOpenDialog("submit");
  };
  const [adminActionDialogOpen, setAdminActionDialogOpen] = useState(false);
  const [adminActionType, setAdminActionType] = useState(null); // "reset" | "reopen"
  const handleAccept = async () => {
    handleOpenDialog("accept");
  };
  const continueSubmitAnyway = async () => {
    setWarningDialogOpen(false);
    handleOpenDialog("submit");
  };

  const continueAcceptAnyway = async () => {
    setWarningDialogOpen(false);
    handleOpenDialog("accept");
  };
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setStartPosition({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const container = imageContainerRef.current;
    const img = document.getElementById("zoomable-image");
    if (!container || !img) return;

    const newX = e.clientX - startPosition.x;
    const newY = e.clientY - startPosition.y;

    const baseWidth = img.clientWidth || img.naturalWidth;
    const baseHeight = img.clientHeight || img.naturalHeight;

    const rotatedWidth =
      normalizedRotation === 90 || normalizedRotation === 270
        ? baseHeight
        : baseWidth;

    const rotatedHeight =
      normalizedRotation === 90 || normalizedRotation === 270
        ? baseWidth
        : baseHeight;

    const visibleWidth = rotatedWidth * zoom;
    const visibleHeight = rotatedHeight * zoom;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    // allow dragging on all 4 sides
    const minPosX = containerWidth - visibleWidth;
    const maxPosX = visibleWidth - containerWidth;

    const minPosY = containerHeight - visibleHeight;
    const maxPosY = visibleHeight - containerHeight;

    setPosition({
      x: clamp(newX, Math.min(minPosX, maxPosX), Math.max(minPosX, maxPosX)),
      y: clamp(newY, Math.min(minPosY, maxPosY), Math.max(minPosY, maxPosY)),
    });
  };
  const handleMouseUp = () => {
    setIsDragging(false);
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const [isLockedByAnotherUser, setIsLockedByAnotherUser] = useState(false);
  const getSecondsSince = (startedAt) => {
    if (!startedAt) return null;
    return Math.max(
      0,
      Math.round((Date.now() - new Date(startedAt).getTime()) / 1000)
    );
  };

  const getTranscriptionElapsedSeconds = () => {
    return getSecondsSince(transcriptionSessionStartedAt);
  };

  const getReviewElapsedSeconds = () => {
    return getSecondsSince(reviewSessionStartedAt);
  };
  useEffect(() => {
    if (pageData && currentUser) {
      const isLocked =
        pageData.isLocked &&
        pageData.lockedBy &&
        pageData.lockedBy !== currentUser._id;

      if (isLocked) {
        setIsLockedByAnotherUser(true);
      } else {
        setIsLockedByAnotherUser(false);
      }
    }
  }, [pageData, currentUser]);

  useEffect(() => {
    const loadPage = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        console.error("No token found");
        return;
      }

      let me = null;

      try {
        const meRes = await axios.get(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        me = meRes.data;
        setCurrentUser(meRes.data);
      } catch (err) {
        console.error("Error fetching user", err);
      }

      try {
        const docRes = await axios.get(
          `${API_BASE_URL}/documents/${documentId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setTotalPages(docRes.data.pages || []);
      } catch (err) {
        console.error("Error fetching document:", err);
      }

      try {
        const res = await axios.get(
          `${API_BASE_URL}/documents/${documentId}/page/${pageNumber}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setPageData(res.data);
        const initialTranscription = res.data.transcription || "";
        setTranscription(initialTranscription);
        setHistory([initialTranscription]);
        setIsLockedByAnotherUser(false);
        setHasStartedTypingTimer(false);

        if (
          res.data.status === "in review" &&
          me?._id !== res.data.submittedBy
        ) {
          setReviewSessionStartedAt(null);
          setReviewTimerPaused(false);
          setTranscriptionSessionStartedAt(null);
        } else if (
          res.data.status === "in progress" ||
          res.data.status === "not started"
        ) {
          setTranscriptionSessionStartedAt(null);
          setReviewSessionStartedAt(null);
          setReviewTimerPaused(false);
        } else {
          setTranscriptionSessionStartedAt(null);
          setReviewSessionStartedAt(null);
          setReviewTimerPaused(false);
        }
      } catch (err) {
        if (err.response?.status === 403) {
          console.warn("Page locked by another user");
          setIsLockedByAnotherUser(true);

          try {
            const publicRes = await axios.get(
              `${API_BASE_URL}/documents/public/documents/${documentId}/page/${pageNumber}`
            );

            setPageData(publicRes.data);
            const initialTranscription = publicRes.data.transcription || "";
            setTranscription(initialTranscription);
            setHistory([initialTranscription]);
            setHasStartedTypingTimer(false);

            if (
              publicRes.data.status === "in review" &&
              me?._id !== publicRes.data.submittedBy
            ) {
              setReviewSessionStartedAt(null);
              setReviewTimerPaused(false);
              setTranscriptionSessionStartedAt(null);
            } else if (
              publicRes.data.status === "in progress" ||
              publicRes.data.status === "not started"
            ) {
              setTranscriptionSessionStartedAt(null);
              setReviewSessionStartedAt(null);
              setReviewTimerPaused(false);
            } else {
              setTranscriptionSessionStartedAt(null);
              setReviewSessionStartedAt(null);
            }
          } catch (publicErr) {
            console.error(
              "Error fetching public locked page preview:",
              publicErr
            );
            setPageData(null);
          }
        } else {
          console.error("Error fetching page:", err);
        }
      }
    };

    loadPage();
  }, [documentId, pageNumber]);

  const fetchPageData = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await axios.get(
        `${API_BASE_URL}/documents/${documentId}/page/${pageNumber}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setPageData(response.data);
      setTranscription(response.data.transcription || "");
      setIsLockedByAnotherUser(false);
    } catch (err) {
      if (err.response?.status === 403) {
        setIsLockedByAnotherUser(true);

        try {
          const publicRes = await axios.get(
            `${API_BASE_URL}/documents/public/documents/${documentId}/page/${pageNumber}`
          );

          setPageData(publicRes.data);
          setTranscription(publicRes.data.transcription || "");
        } catch (publicErr) {
          console.error("Error fetching public preview page:", publicErr);
        }
      } else {
        console.error("Error fetching page data:", err);
      }
    }
  };

  const confirmSave = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const formData = new FormData();
      formData.append("text", transcription);

      if (userImage?.length > 0) {
        userImage.forEach((file) => formData.append("images", file));
      }

      for (const imgUrl of removedImages) {
        await axios.delete(
          `${API_BASE_URL}/documents/${documentId}/page/${pageNumber}/image`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            params: { imageUrl: imgUrl },
          }
        );
      }

      const response = await axios.post(
        `${API_BASE_URL}/documents/${documentId}/page/${pageNumber}/transcribe`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );
      setPageData((prev) => ({
        ...prev,
        attachments: response.data.attachments,
      }));

      setMessage(response.data.message);
      setHistory((prevHistory) => [...prevHistory, transcription]);
      setRedoStack([]);
      setIsEditing(false);

      await fetchPageData();
      setUserImage([]);
      setRemovedImages([]);
      setSuccessMessage("Transcription saved successfully");
      setSuccessDialog(true);
    } catch (err) {
      console.error("Error saving transcription:", err);
      setMessage("Failed to save transcription.");
    } finally {
      setOpenDialog(false);
    }
  };
  const startTranscriptionTimingIfNeeded = async () => {
    if (hasStartedTypingTimer) return;
    if (!pageData) return;
    if (pageData.status === "completed" || pageData.status === "in review")
      return;
    if (isLockedByAnotherUser) return;

    try {
      await axios.post(
        `${API_BASE_URL}/documents/${documentId}/page/${pageNumber}/start-transcription`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setHasStartedTypingTimer(true);
      setTranscriptionSessionStartedAt((prev) => prev || new Date());
    } catch (err) {
      console.error("Failed to start transcription timing from typing:", err);
    }
  };
  const pauseReviewTiming = async () => {
    // Already paused / not running
    if (!reviewSessionStartedAt) {
      return;
    }

    try {
      const token = localStorage.getItem("token");

      await axios.post(
        `${API_BASE_URL}/documents/${documentId}/page/${pageNumber}/pause-review`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setReviewSessionStartedAt(null);
      setReviewTimerPaused(true);
    } catch (err) {
      console.error("Failed to pause review timing:", err);
    }
  };
  const resumeReviewTiming = async () => {
    // Don't start another timer if review is already running
    if (reviewSessionStartedAt) {
      return;
    }

    // Only run when the page is actually in review
    if (pageData?.status !== "in review") {
      return;
    }

    // The person who submitted the transcription
    // should not review their own page
    if (
      currentUser?._id &&
      pageData?.submittedBy &&
      String(currentUser._id) === String(pageData.submittedBy)
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${API_BASE_URL}/documents/${documentId}/page/${pageNumber}/start-review`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.ok === false) {
        console.error("Could not resume review:", response.data?.reason);
        return;
      }

      // Start a NEW frontend review session
      setReviewSessionStartedAt(new Date());

      // Reviewer is active again
      setReviewTimerPaused(false);
    } catch (err) {
      console.error("Failed to resume review timing:", err);
    }
  };
  const clearReviewIdleTimer = () => {
    if (reviewIdleTimerRef.current) {
      clearTimeout(reviewIdleTimerRef.current);
      reviewIdleTimerRef.current = null;
    }
  };
  const scheduleReviewIdleCheck = () => {
    clearReviewIdleTimer();

    if (pageData?.status !== "in review") {
      return;
    }

    if (!reviewSessionStartedAt) {
      return;
    }

    reviewIdleTimerRef.current = setTimeout(async () => {
      // IMPORTANT:
      // Pause first so time does not continue while
      // we're waiting for the user to answer.
      await pauseReviewTiming();

      setReviewIdleDialogOpen(true);
    }, REVIEW_IDLE_TIMEOUT);
  };
  const goToPage = async () => {
    await leaveReviewPage(`/document/${documentId}/page/${selectedPage}`);
  };
  const goToPrevPage = async () => {
    if (selectedPage > 1) {
      const newPage = selectedPage - 1;

      setSelectedPage(newPage);

      await leaveReviewPage(`/document/${documentId}/page/${newPage}`);
    }
  };
  const goToNextPage = async () => {
    if (selectedPage < totalPages.length) {
      const newPage = selectedPage + 1;

      setSelectedPage(newPage);

      await leaveReviewPage(`/document/${documentId}/page/${newPage}`);
    }
  };

  const handleUndo = () => {
    if (history.length > 1) {
      const lastState = history[history.length - 1];
      const newHistory = history.slice(0, history.length - 1);
      setRedoStack([transcription, ...redoStack]);
      setTranscription(newHistory[newHistory.length - 1]);
      setHistory([newHistory]);
    }
  };

  const handleRedo = () => {
    if (redoStack.length > 0) {
      const latestRedo = redoStack[0];
      const newRedoStack = redoStack.slice(1);

      setHistory((prevHistory) => [...prevHistory, transcription]);
      setTranscription(latestRedo);
      setRedoStack([newRedoStack]);
    }
  };

  const confirmSubmitForReview = async () => {
    try {
      await axios.patch(
        `${API_BASE_URL}/documents/${documentId}/page/${pageNumber}/status`,
        { status: "in review" },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          withCredentials: true,
        }
      );
      setMessage("Submitted for review.");
      await fetchPageData();
      setTranscriptionSessionStartedAt(null);
      setReviewSessionStartedAt(null);
      setSuccessMessage("Page submitted for review.");
      setSuccessDialog(true);
    } catch (err) {
      console.error("Error submitting for review:", err);
    } finally {
      setOpenDialog(false);
    }
  };

  const confirmAccept = async () => {
    try {
      clearReviewIdleTimer();

      if (reviewSessionStartedAt) {
        await pauseReviewTiming();
      }

      await axios.patch(
        `${API_BASE_URL}/documents/${documentId}/page/${pageNumber}/status`,
        { status: "completed" },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setMessage("Page marked as completed.");

      await fetchPageData();

      setReviewSessionStartedAt(null);
      setReviewTimerPaused(false);
      setReviewIdleDialogOpen(false);
      setSuccessMessage("Page marked as completed.");
      setSuccessDialog(true);
    } catch (err) {
      console.error("Error updating status:", err);
    } finally {
      setOpenDialog(false);
    }
  };

  const handleEdit = async () => {
    try {
      clearReviewIdleTimer();

      if (reviewSessionStartedAt) {
        await pauseReviewTiming();
      }

      await axios.patch(
        `${API_BASE_URL}/documents/${documentId}/page/${pageNumber}/status`,
        { status: "in progress" },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setMessage("Page moved to in progress.");
      setIsEditing(true);
      setTranscriptionSessionStartedAt(null);
      setHasStartedTypingTimer(false);
      setReviewSessionStartedAt(null);
      setReviewTimerPaused(false);
      setReviewIdleDialogOpen(false);
      await fetchPageData();
    } catch (err) {
      if (err.response?.status === 409) {
        setSuccessMessage(
          "Another volunteer is currently working on this page. Please pick a different one."
        );
        setSuccessDialog(true);
      } else {
        console.error("Error updating status:", err);
      }
    }
  };
  const handleAdminMoveToInProgress = async () => {
    try {
      await axios.patch(
        `${API_BASE_URL}/documents/${documentId}/page/${pageNumber}/status`,
        { status: "in progress" },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setMessage("Page moved back to in progress.");
      await fetchPageData();
      setSuccessMessage("Page moved back to in progress.");
      setSuccessDialog(true);
    } catch (err) {
      console.error("Error moving page back to in progress:", err);
      setSuccessMessage(
        err.response?.data?.error || "Failed to move page back to in progress."
      );
      setSuccessDialog(true);
    }
  };

  const handleAdminResetToNotStarted = async () => {
    try {
      await axios.patch(
        `${API_BASE_URL}/documents/${documentId}/page/${pageNumber}/status`,
        { status: "not started" },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setMessage("Page reset to not started.");
      await fetchPageData();
      setSuccessMessage("Page reset to not started.");
      setSuccessDialog(true);
    } catch (err) {
      console.error("Error resetting page:", err);
      setSuccessMessage(err.response?.data?.error || "Failed to reset page.");
      setSuccessDialog(true);
    }
  };
  const handleCropAndExtractText = async () => {
    if (!imageForCrop) {
      console.warn("No image selected for crop");
      return;
    }
    setIsExtracting(true);
    setOcrError("");
    setOcrInfo("");
    setOcrResult("");

    const imgEl = document.getElementById("zoomable-image");
    if (!imgEl) {
      console.error("Could not find #zoomable-image in DOM");
      setIsExtracting(false);
      return;
    }
    const dispW = imgEl.clientWidth,
      natW = imgEl.naturalWidth;
    const dispH = imgEl.clientHeight,
      natH = imgEl.naturalHeight;

    const scaleX = natW / dispW;
    const scaleY = natH / dispH;

    const raw = {
      x: Math.round(cropBox.x * scaleX),
      y: Math.round(cropBox.y * scaleY),
      width: Math.round(cropBox.width * scaleX),
      height: Math.round(cropBox.height * scaleY),
    };

    const safeCrop = {
      x: Math.max(0, Math.min(raw.x, natW - 1)),
      y: Math.max(0, Math.min(raw.y, natH - 1)),
      width: Math.max(1, Math.min(raw.width, natW - raw.x)),
      height: Math.max(1, Math.min(raw.height, natH - raw.y)),
    };

    try {
      const res = await axios.post(
        `${API_BASE_URL}/documents/azure/ocr`,
        { imageUrl: imageForCrop, crop: safeCrop },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setOcrResult(res.data.text);
    } catch (err) {
      console.error("OCR Crop POST failed:", {
        url: `${API_BASE_URL}/documents/azure/ocr`,
        payload: { imageUrl: imageForCrop, crop: safeCrop },
        status: err.response?.status,
        response: err.response?.data,
        message: err.message,
      });

      const status = err.response?.status;
      const data = err.response?.data;

      if (status === 429) {
        if (data?.retryAfterMs) {
          const seconds = Math.ceil(data.retryAfterMs / 1000);
          setOcrCooldownUntil(Date.now() + data.retryAfterMs);
          setOcrInfo(`Please wait ${seconds} seconds before using OCR again.`);
        } else {
          setOcrInfo(
            data?.error ||
              "OCR is currently busy. Please wait a few seconds and try again."
          );
        }
      } else if (status === 400) {
        setOcrError(
          data?.hint ||
            data?.error ||
            "The selected area could not be processed."
        );
      } else {
        setOcrError(data?.error || "OCR failed. Please try again.");
      }
    } finally {
      setIsExtracting(false);
    }
  };
  const handleBackToAllPages = async () => {
    clearReviewIdleTimer();

    if (reviewSessionStartedAt) {
      await pauseReviewTiming();
    }

    const token = localStorage.getItem("token");

    if (token && currentUser && lastLockedPageRef.current) {
      try {
        await axios.post(
          `${API_BASE_URL}/documents/${documentId}/page/${lastLockedPageRef.current}/unlock`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log("Unlocked before navigating to all pages");
      } catch (err) {
        console.error("Error unlocking on navigation", err);
      }
    }
    navigate(`/transcription/documents/${documentId}`);
  };
  const isSaveEnabled = () => {
    const transcriptionChanged =
      transcription && transcription !== pageData.transcription;
    const imagesChanged = userImage.length > 0 || removedImages.length > 0;
    return transcriptionChanged || imagesChanged;
  };
  const handleContinueReviewing = async () => {
    setReviewIdleDialogOpen(false);

    await resumeReviewTiming();
  };
  const handleStayPaused = () => {
    setReviewIdleDialogOpen(false);

    setReviewTimerPaused(true);

    clearReviewIdleTimer();
  };
  const leaveReviewPage = async (destination) => {
    clearReviewIdleTimer();

    if (reviewSessionStartedAt) {
      await pauseReviewTiming();
    }

    navigate(destination);
  };
  const isSubmitForReviewEnabled = () => {
    return transcription.length > 0;
  };
  const handleWheel = (e) => {
    e.preventDefault();

    const container = imageContainerRef.current;
    const img = document.getElementById("zoomable-image");
    if (!container || !img) return;

    const scaleAmount = 0.1;
    const zoomDirection = e.deltaY < 0 ? 1 : -1;
    const newZoom = clamp(zoom + zoomDirection * scaleAmount, 1, 5);

    const rect = img.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const baseWidth = img.clientWidth / zoom;
    const baseHeight = img.clientHeight / zoom;

    const rotatedWidth =
      normalizedRotation === 90 || normalizedRotation === 270
        ? baseHeight
        : baseWidth;

    const rotatedHeight =
      normalizedRotation === 90 || normalizedRotation === 270
        ? baseWidth
        : baseHeight;

    const prevWidth = rotatedWidth * zoom;
    const prevHeight = rotatedHeight * zoom;

    const nextWidth = rotatedWidth * newZoom;
    const nextHeight = rotatedHeight * newZoom;

    const scaleX = prevWidth ? mouseX / prevWidth : 0;
    const scaleY = prevHeight ? mouseY / prevHeight : 0;

    const rawX = position.x - (nextWidth - prevWidth) * scaleX;
    const rawY = position.y - (nextHeight - prevHeight) * scaleY;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    const minPosX = containerWidth - nextWidth;
    const maxPosX = nextWidth - containerWidth;

    const minPosY = containerHeight - nextHeight;
    const maxPosY = nextHeight - containerHeight;

    setPosition({
      x: clamp(rawX, Math.min(minPosX, maxPosX), Math.max(minPosX, maxPosX)),
      y: clamp(rawY, Math.min(minPosY, maxPosY), Math.max(minPosY, maxPosY)),
    });

    setZoom(newZoom);
  };

  useEffect(() => {
    if (pageData?.imageUrl) {
      setBrightness(100);
      setContrast(100);
      setInvert(0);
      setZoom(1);
      setRotation(0);
      setPosition({ x: 0, y: 0 });
    }
  }, [pageData?.imageUrl]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseUp]);
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (
        currentUser &&
        pageData?.lockedBy === currentUser._id &&
        pageData?.isLocked
      ) {
        const url = `${API_BASE_URL}/documents/${documentId}/page/${pageNumber}/unlock`;
        const headers = { type: "application/json" };
        const blob = new Blob([], headers);
        navigator.sendBeacon(url, blob);
        console.log("Page unlocked via beacon on unload");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [currentUser, pageData, documentId, pageNumber]);

  const handleFiltersClick = (event) => {
    setFiltersAnchor(event.currentTarget);
  };
  const handleFiltersClose = () => {
    setFiltersAnchor(null);
  };
  const hasUnlockedRef = useRef(false);
  useEffect(() => {
    return () => {
      const token = localStorage.getItem("token");
      if (token && currentUser && lastLockedPageRef.current) {
        axios
          .post(
            `${API_BASE_URL}/documents/${documentId}/page/${lastLockedPageRef.current}/unlock`,
            {},
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          )
          .then(() => {
            console.log("Page unlocked on exit");
          })
          .catch((err) => {
            console.error("Error unlocking page on exit", err);
          });
      }
    };
  }, [currentUser, documentId]);

  useEffect(() => {
    if (
      currentUser &&
      pageData &&
      !isLockedByAnotherUser &&
      pageData.lockedBy !== currentUser._id
    ) {
      axios
        .post(
          `${API_BASE_URL}/documents/${documentId}/page/${pageNumber}/lock`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        )
        .then(() => {
          console.log("Page locked by current user");
          lastLockedPageRef.current = pageNumber;
        })
        .catch((err) => console.error("Failed to lock page", err));
    }
  }, [currentUser, pageData, isLockedByAnotherUser, documentId, pageNumber]);

  useEffect(() => {
    const token = localStorage.getItem("token");

    const unlockPreviousPage = async () => {
      if (!token || !currentUser) return;

      if (
        lastLockedPageRef.current &&
        lastLockedPageRef.current !== Number(pageNumber)
      ) {
        try {
          await axios.post(
            `${API_BASE_URL}/documents/${documentId}/page/${lastLockedPageRef.current}/unlock`,
            {},
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          console.log(`Unlocked previous page: ${lastLockedPageRef.current}`);
        } catch (err) {
          console.error("Error unlocking previous page", err);
        }
      }
    };

    unlockPreviousPage();
  }, [pageNumber, currentUser, documentId]);
  useEffect(() => {
    if (selectArea) {
      console.log("Starting demo animation");
      setShowDemo(true);
      const t = setTimeout(() => {
        console.log("Hiding demo animation");
        setShowDemo(false);
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [selectArea]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setRunTour(true);
    }, 200);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    if (ocrResult) {
      ocrResultRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [ocrResult]);
  useEffect(() => {
    if (imageContainerRef.current) {
      const updateSize = () => {
        const rect = imageContainerRef.current.getBoundingClientRect();
        setImageContainerSize({ width: rect.width, height: rect.height });
      };
      updateSize();
      window.addEventListener("resize", updateSize);
      return () => window.removeEventListener("resize", updateSize);
    }
  }, []);
  useEffect(() => {
    if (selectArea) setRunTour(true);
  }, [selectArea]);
  useEffect(() => {
    if (!ocrCooldownUntil) return;

    const interval = setInterval(() => {
      const remaining = Math.max(
        0,
        Math.ceil((ocrCooldownUntil - Date.now()) / 1000)
      );
      setOcrSecondsLeft(remaining);

      if (remaining <= 0) {
        setOcrCooldownUntil(null);
        setOcrSecondsLeft(0);
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [ocrCooldownUntil]);
  useEffect(() => {
    setSelectedPage(parseInt(pageNumber, 10));
  }, [pageNumber]);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if (["finished", "skipped"].includes(status)) {
      setRunTour(false);
    }
  };

  const CustomBeacon = ({ onClick }) => (
    <Box
      onClick={onClick}
      sx={{
        ml: 2,
        width: 40,
        height: 40,
        borderRadius: "50%",
        backgroundColor: "rgba(25, 118, 210, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        zIndex: 10000,
      }}
    >
      <HelpOutlineIcon sx={{ color: "#fff" }} />
    </Box>
  );
  const handleToggleFavorite = async () => {
    try {
      setFavoriteLoading(true);

      const nextValue = !isFavorite;

      const res = await axios.patch(
        `${API_BASE_URL}/user-activity/favorite/${documentId}/${pageNumber}`,
        {
          isFavorite: nextValue,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setIsFavorite(res.data.isFavorite);
    } catch (err) {
      console.error("Failed to update favorite:", err);
    } finally {
      setFavoriteLoading(false);
    }
  };
  const getDisplaySize = () => {
    const img = imgRef.current;
    if (!img) return { width: 0, height: 0 };

    const baseWidth = img.clientWidth;
    const baseHeight = img.clientHeight;

    const scaledWidth = baseWidth * zoom;
    const scaledHeight = baseHeight * zoom;

    return { width: scaledWidth, height: scaledHeight };
  };

  const getRotationOffset = () => {
    const img = imgRef.current;
    if (!img) return { x: 0, y: 0 };

    const width = img.clientWidth;
    const height = img.clientHeight;

    switch (normalizedRotation) {
      case 90:
        return { x: height, y: 0 };
      case 180:
        return { x: width, y: height };
      case 270:
        return { x: 0, y: width };
      default:
        return { x: 0, y: 0 };
    }
  };

  const rotationOffset = getRotationOffset();
  const centerImageForCurrentRotation = () => {
    const img = imgRef.current;
    const container = imageContainerRef.current;
    if (!img || !container) return;

    const imgWidth = img.clientWidth;
    const imgHeight = img.clientHeight;

    const rotatedWidth =
      normalizedRotation === 90 || normalizedRotation === 270
        ? imgHeight
        : imgWidth;

    const rotatedHeight =
      normalizedRotation === 90 || normalizedRotation === 270
        ? imgWidth
        : imgHeight;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    const centeredX = Math.max(0, (containerWidth - rotatedWidth) / 2);
    const centeredY = Math.max(0, (containerHeight - rotatedHeight) / 2);

    setPosition({
      x: centeredX,
      y: centeredY,
    });
  };
  useEffect(() => {
    if (!pageData?.imageUrl) return;

    const timer = setTimeout(() => {
      if (normalizedRotation === 90 || normalizedRotation === 270) {
        centerImageForCurrentRotation();
      } else {
        setPosition({ x: 0, y: 0 });
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [normalizedRotation, pageData?.imageUrl]);
  useEffect(() => {
    if (pageData?.status !== "in review") {
      return;
    }

    if (
      currentUser?._id &&
      pageData?.submittedBy &&
      String(currentUser._id) === String(pageData.submittedBy)
    ) {
      return;
    }

    const handleActivity = () => {
      // Don't automatically resume while the
      // "Are you still reviewing?" dialog is open.
      if (reviewIdleDialogOpen) {
        return;
      }

      scheduleReviewIdleCheck();
    };

    const activityEvents = ["pointerdown", "keydown", "scroll"];
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, {
        passive: true,
      });
    });

    scheduleReviewIdleCheck();

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });

      clearReviewIdleTimer();
    };
  }, [
    pageData?.status,
    pageData?.submittedBy,
    currentUser?._id,
    reviewIdleDialogOpen,
    reviewSessionStartedAt,
    reviewTimerPaused,
  ]);
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (pageData?.status !== "in review") {
        return;
      }

      if (
        currentUser?._id &&
        pageData?.submittedBy &&
        String(currentUser._id) === String(pageData.submittedBy)
      ) {
        return;
      }

      if (document.hidden) {
        // User left this browser tab.
        clearReviewIdleTimer();

        await pauseReviewTiming();

        return;
      }

      // User returned to the tab.
      //
      // Don't silently resume.
      // We want them to explicitly indicate that
      // they're reviewing again.
      if (!reviewSessionStartedAt) {
        setReviewIdleDialogOpen(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    pageData?.status,
    pageData?.submittedBy,
    currentUser?._id,
    reviewSessionStartedAt,
  ]);
  useEffect(() => {
    if (!pageData || !currentUser) return;

    if (pageData.status !== "in review") return;

    // Cannot review own transcription
    if (
      pageData.submittedBy &&
      String(currentUser._id) === String(pageData.submittedBy)
    ) {
      return;
    }

    // Don't start while idle dialog is showing
    if (reviewIdleDialogOpen) return;

    // Already running
    if (reviewSessionStartedAt) return;

    // User explicitly chose to remain paused
    if (reviewTimerPaused) return;

    resumeReviewTiming();
  }, [pageData?.status, pageData?.submittedBy, currentUser?._id]);
  useEffect(() => {
    clearReviewIdleTimer();

    setReviewSessionStartedAt(null);
    setReviewTimerPaused(false);
    setReviewIdleDialogOpen(false);
  }, [documentId, pageNumber]);
  useEffect(() => {
    const recordPageView = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) return;

        await axios.post(
          `${API_BASE_URL}/user-activity/view/${documentId}/${pageNumber}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } catch (err) {
        console.error("Failed to record page view:", err);
      }
    };

    recordPageView();
  }, [documentId, pageNumber]);

  return (
    <Box
      sx={{
        width: "auto",
        padding: { xs: "20px", sm: "30px", md: "1px 80px" },
        border: "none",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
        position: "relative",
      }}
    >
      <Joyride
        steps={steps}
        run={runTour}
        callback={handleJoyrideCallback}
        showSkipButton
        continuous
        beaconComponent={CustomBeacon}
        styles={{
          options: {
            primaryColor: "#1976d2",
            zIndex: 10000,
          },
          tooltipContainer: {
            borderRadius: "8px",
            fontSize: "14px",
          },
        }}
      />
      <Box sx={{ mt: 1, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToAllPages}
          sx={{
            minHeight: 44,
            px: 1.5,

            color: "#111111",
            backgroundColor: "#fff7db",
            borderColor: "#e6dcae",

            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,

            "&:hover": {
              backgroundColor: "#fff1bf",
              borderColor: "#d6c98e",
            },

            "&:focus-visible": {
              outline: "3px solid #786d3e",
              outlineOffset: "2px",
            },
          }}
        >
          View All Pages
        </Button>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          marginBottom: "20px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="h6">Page</Typography>

          <Select
            value={selectedPage}
            onChange={(e) => setSelectedPage(parseInt(e.target.value))}
          >
            {totalPages.map((page) => (
              <MenuItem key={page.pageNumber} value={page.pageNumber}>
                {page.pageNumber}
              </MenuItem>
            ))}
          </Select>

          <Button onClick={goToPage} variant="contained">
            Go
          </Button>

          <IconButton onClick={goToPrevPage} disabled={selectedPage === 1}>
            <ArrowBack />
          </IconButton>

          <IconButton
            onClick={goToNextPage}
            disabled={selectedPage === totalPages.length}
          >
            <ArrowForward />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          {/* Save for Later */}
          <Button
            variant={isFavorite ? "contained" : "outlined"}
            startIcon={isFavorite ? <BookmarkIcon /> : <BookmarkBorderIcon />}
            onClick={handleToggleFavorite}
            disabled={favoriteLoading}
            sx={{
              minHeight: 44,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            {isFavorite ? "Saved for Later" : "Save for Later"}
          </Button>

          {/* Divider */}
          <Box
            aria-hidden="true"
            sx={{
              width: "1px",
              height: 24,
              backgroundColor: "#d9d9d9",
              mx: 0.5,
              display: {
                xs: "none",
                sm: "block",
              },
            }}
          />

          {/* My Activity */}
          <Button
            variant="text"
            onClick={() => leaveReviewPage("/transcription/my-activity")}
            endIcon={
              <ArrowForward
                sx={{
                  fontSize: "18px !important",
                }}
              />
            }
            sx={{
              minHeight: 44,
              px: 1.25,

              color: "#5f5740",

              textTransform: "none",
              fontWeight: 600,

              borderRadius: 2,

              "&:hover": {
                color: "#3f3a20",
                backgroundColor: "#fff7db",
              },

              "&:focus-visible": {
                outline: "3px solid #786d3e",
                outlineOffset: "2px",
              },
            }}
          >
            My Activity
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          width: "100%",
          height: {
            xs: "auto",
            md: "auto",
            lg: "auto",
          },
          gap: isMobile ? 1 : 4,
          p: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            maxWidth: isMobile ? "100%" : "50%",
            p: 3,
            borderRight: isMobile ? "none" : "1px solid gray",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            overflow: "visible",
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              position: "relative",
              zIndex: 10,
              mb: 4,
            }}
          >
            <Button variant="contained" onClick={handleFiltersClick}>
              Filters
            </Button>

            <Button
              variant="contained"
              onClick={() => setZoom((prev) => Math.min(prev + 0.1, 5))}
            >
              Zoom In
            </Button>

            <Button
              variant="contained"
              onClick={() => setZoom((prev) => Math.max(prev - 0.1, 1))}
            >
              Zoom Out
            </Button>

            <Button
              variant="contained"
              onClick={() => {
                setRotation((prev) => prev - 90);
              }}
            >
              Rotate Left
            </Button>

            <Button
              variant="contained"
              onClick={() => {
                setRotation((prev) => prev + 90);
              }}
            >
              Rotate Right
            </Button>

            <Button
              variant="contained"
              onClick={() => {
                setBrightness(100);
                setContrast(100);
                setInvert(0);
                setZoom(1);
                setRotation(0);
                setPosition({ x: 0, y: 0 });
              }}
            >
              Reset
            </Button>
          </Box>

          <Popover
            open={Boolean(filtersAnchor)}
            anchorEl={filtersAnchor}
            onClose={handleFiltersClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            sx={{ zIndex: 1000 }}
          >
            <Box
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Button
                onClick={handleFiltersClose}
                sx={{ alignSelf: "flex-end" }}
              >
                x
              </Button>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography sx={{ fontSize: 12 }}>
                  {" "}
                  Brightness: {brightness}%
                </Typography>
                <Slider
                  value={brightness}
                  onChange={(e, value) => setBrightness(value)}
                  min={50}
                  max={200}
                  size="small"
                />
                <Typography sx={{ fontSize: 12 }}>
                  {" "}
                  Contrast: {contrast}%
                </Typography>
                <Slider
                  value={contrast}
                  onChange={(e, value) => setContrast(value)}
                  min={50}
                  max={200}
                  size="small"
                />
                <Typography sx={{ fontSize: 12 }}>
                  {" "}
                  Invert: {invert}%
                </Typography>
                <Slider
                  value={invert}
                  onChange={(e, value) => setInvert(value)}
                  min={0}
                  max={100}
                  size="small"
                />
              </Box>
            </Box>
          </Popover>
          {pageData?.imageUrl ? (
            <Box
              ref={imageContainerRef}
              sx={{
                position: "relative",
                width: "100%",
                minHeight: isQuarterTurn ? "650px" : "500px",
                border: "1px solid gray",
                borderRadius: "5px",
                overflow: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",

                backgroundColor: "#fafafa",
                p: 4,
                boxSizing: "border-box",
                overflow: "auto",
              }}
            >
              {pageData?.referenceUrl && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    zIndex: 20,
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<OpenInNewIcon fontSize="small" />}
                    onClick={() => {
                      const url = pageData.referenceUrl.startsWith("http")
                        ? pageData.referenceUrl
                        : `https://${pageData.referenceUrl}`;
                      window.open(url, "_blank", "noopener,noreferrer");
                    }}
                    sx={{
                      textTransform: "none",
                      borderRadius: 999,
                      px: 1.5,
                      py: 0.75,
                      fontWeight: 700,
                      boxShadow: "0 10px 24px rgba(0,0,0,0.25)",
                      backdropFilter: "blur(6px)",
                    }}
                  >
                    Source
                  </Button>
                </Box>
              )}
              <img
                id="zoomable-image"
                ref={imgRef}
                src={pageData.imageUrl}
                alt={`Page ${pageNumber}`}
                onMouseDown={handleMouseDown}
                onWheel={handleWheel}
                style={{
                  transform: `
  translate(${position.x}px, ${position.y}px)
  translate(${rotationOffset.x}px, ${rotationOffset.y}px)
  rotate(${rotation}deg)
  scale(${zoom})
`,
                  transformOrigin: "top left",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  filter: `brightness(${brightness}%) contrast(${contrast}%) invert(${invert}%)`,
                  minHeight: isQuarterTurn ? "auto" : "300px",
                  maxWidth: isQuarterTurn ? "none" : "100%",
                  maxHeight: isQuarterTurn ? "none" : "80vh",
                  width: isQuarterTurn ? "70vh" : "auto",
                  height: "auto",
                  cursor: isDragging ? "grabbing" : "grab",
                  transition: isDragging
                    ? "none"
                    : "transform 0.2s ease-in-out",
                  display: "block",
                }}
              />
              {/* {pageData?.contentType && (
                <Chip
                  label={`Type: ${pageData.contentType}`}
                  size="small"
                  color={
                    pageData.contentType === "printed"
                      ? "success"
                      : pageData.contentType === "handwritten"
                        ? "secondary"
                        : "default"
                  }
                  sx={{ mb: 1 }}
                />
              )} */}

              {selectArea && (
                <Rnd
                  id="joy-crop-box"
                  bounds="parent"
                  size={{ width: cropBox.width, height: cropBox.height }}
                  position={{ x: cropBox.x, y: cropBox.y }}
                  onDragStop={(_, d) =>
                    setCropBox((b) => ({ ...b, x: d.x, y: d.y }))
                  }
                  onResizeStop={(_, __, ref, ___, pos) =>
                    setCropBox({
                      width: ref.offsetWidth,
                      height: ref.offsetHeight,
                      x: pos.x,
                      y: pos.y,
                    })
                  }
                  style={{
                    border: "2px dashed rgba(255,255,255,0.9)",
                    background: "rgba(0,0,0,0.2)",
                    zIndex: 10,
                  }}
                />
              )}
            </Box>
          ) : (
            <Typography>No preview available</Typography>
          )}
          <Box
            id="joy-select-area"
            component="span"
            sx={{ display: "inline-block", ml: 1 }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectArea}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setSelectArea(isChecked);

                    if (isChecked) setImageForCrop(pageData.imageUrl);
                  }}
                />
              }
              label={
                <>
                  Select area for OCR&nbsp;{" "}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                  >
                    (powered by Azure Computer Vision){" "}
                  </Typography>
                </>
              }
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            We send this selection securely to Azure for text extraction. We
            don't store or own images or text.
          </Typography>
          {selectArea && (
            <Button
              variant="contained"
              sx={{
                mt: 2,
                backgroundColor: "#facc15",
                color: "#78350f",
                "&:hover": { backgroundColor: "#eab308" },
              }}
              onClick={handleCropAndExtractText}
              disabled={isExtracting || ocrSecondsLeft > 0}
            >
              {isExtracting ? (
                <>
                  Processing...
                  <CircularProgress
                    size={16}
                    sx={{ ml: 1, color: "#78350f" }}
                  />
                </>
              ) : ocrSecondsLeft > 0 ? (
                `Wait ${ocrSecondsLeft}s`
              ) : (
                "Extract Text"
              )}
            </Button>
          )}
          {ocrInfo && (
            <Typography
              variant="body2"
              sx={{ mt: 1.5, color: "warning.main", textAlign: "center" }}
            >
              {ocrInfo}
            </Typography>
          )}

          {ocrError && (
            <Typography
              variant="body2"
              sx={{ mt: 1.5, color: "error.main", textAlign: "center" }}
            >
              {ocrError}
            </Typography>
          )}
          {selectArea && ocrResult && (
            <Box ref={ocrResultRef} sx={{ mt: 3 }}>
              <Typography variant="subtitle1">Extracted Text:</Typography>
              <TextField
                multiline
                rows={10}
                value={ocrResult}
                InputProps={{ readOnly: true }}
                sx={{
                  mt: 1,
                  minHeight: 200,
                  width: { xs: "100%", sm: 400, md: 500 },
                }}
              />

              <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
                <Button
                  variant="outlined"
                  sx={{ mt: 1 }}
                  onClick={() => {
                    navigator.clipboard.writeText(ocrResult);
                    setCopySuccess(true);
                    setTimeout(() => setCopySuccess(false), 2000);
                  }}
                >
                  {copySucess ? "Copied!" : "Copy to Clipboard"}
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            maxWidth: isMobile ? "100%" : "50%",
            p: 3,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Status helper panel + Tips / How-To side-by-side */}{" "}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "flex-start" },
              gap: 2,
              mb: 2,
            }}
          >
            {/* Status card */}{" "}
            {statusMeta && (
              <Box
                sx={{
                  flex: 1,
                  p: 1.5,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor:
                    statusMeta.color === "success"
                      ? "success.light"
                      : statusMeta.color === "warning"
                        ? "warning.light"
                        : statusMeta.color === "info"
                          ? "info.light"
                          : "grey.300",
                  backgroundColor:
                    statusMeta.color === "success"
                      ? "success.lighter"
                      : statusMeta.color === "warning"
                        ? "warning.lighter"
                        : statusMeta.color === "info"
                          ? "info.lighter"
                          : "grey.50",
                }}
              >
                {" "}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 0.5,
                    gap: 1,
                  }}
                >
                  {/* Left: icon + status title */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {statusMeta.icon}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {statusMeta.title}
                    </Typography>
                  </Box>

                  {/* Right: registered contributors */}
                  {statusMeta.contributorsLine && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 2, whiteSpace: "nowrap" }}
                    >
                      {statusMeta.contributorsLine}
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {statusMeta.message}{" "}
                </Typography>{" "}
              </Box>
            )}
            {/* Tips + How-To buttons */}{" "}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                minWidth: 170,
              }}
            >
              {" "}
              <Button
                size="small"
                variant="contained"
                color="info"
                startIcon={<TipsAndUpdatesIcon />}
                onClick={() => {
                  setActiveTipIndex(0);
                  setTipsOpen(true);
                }}
                sx={{
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: 2,
                }}
              >
                Tips{" "}
              </Button>{" "}
              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={<HelpOutlineIcon />}
                onClick={() => setGuideOpen(true)}
                sx={{
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: 2,
                }}
              >
                How-To Guide{" "}
              </Button>{" "}
            </Box>{" "}
          </Box>{" "}
          {/* Main transcription box – no labels like "Status:" or "Transcribe this page:" */}{" "}
          {pageData?.qualityFlags?.flaggedForAttention && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "warning.light",
                backgroundColor: "#fff8e1",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Needs Attention
              </Typography>
              <Typography variant="body2">
                {pageData?.qualityFlags?.lastFlagReason ||
                  "This page was completed unusually quickly and may need a closer look."}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={Math.floor((imageContainerSize.height || 450) / 24)}
            value={transcription}
            onChange={async (e) => {
              const value = e.target.value;

              if (!hasStartedTypingTimer && value !== transcription) {
                await startTranscriptionTimingIfNeeded();
              }

              setTranscription(value);
            }}
            disabled={
              pageData?.status === "completed" ||
              pageData?.status === "in review"
            }
          />{" "}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Contributors:{" "}
            {pageData?.contributors?.map((c) => c.name).join(", ") ||
              "None"}{" "}
          </Typography>
          <Typography sx={{ mt: 2 }}>Attach images (optional):</Typography>{" "}
          <Typography variant="caption" color="text.secondary">
            Use snipping tool to take screenshots and attach here.{" "}
          </Typography>{" "}
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) =>
              setUserImage((prev) => [...prev, ...Array.from(e.target.files)])
            }
          />{" "}
          {userImage.length > 0 && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
              {" "}
              {userImage.map((file, idx) => (
                <Box key={idx} sx={{ position: "relative" }}>
                  {" "}
                  <img
                    src={URL.createObjectURL(file)}
                    alt="Preview"
                    style={{
                      width: "120px",
                      border: "1px solid #ccc",
                      borderRadius: "6px",
                    }}
                  />{" "}
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      backgroundColor: "rgba(0,0,0,0.6)",
                      color: "#fff",
                      borderRadius: "50%",
                      p: "2px",
                      "&:hover": { backgroundColor: "rgba(255,0,0,0.8)" },
                    }}
                    onClick={() => {
                      setUserImage((prev) => prev.filter((_, i) => i !== idx));
                    }}
                  >
                    <CloseIcon fontSize="small" />{" "}
                  </IconButton>{" "}
                </Box>
              ))}{" "}
            </Box>
          )}{" "}
          {pageData?.attachments?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">Attached Images:</Typography>{" "}
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {" "}
                {pageData.attachments.map((imgUrl, idx) => (
                  <Box key={idx} sx={{ position: "relative" }}>
                    {" "}
                    <img
                      src={imgUrl}
                      alt="Attachment"
                      style={{
                        width: "120px",
                        border: "1px solid #ccc",
                        borderRadius: "6px",
                      }}
                    />{" "}
                    <IconButton
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        backgroundColor: "rgba(0,0,0,0.6)",
                        color: "#fff",
                        borderRadius: "50%",
                        p: "2px",
                        "&:hover": { backgroundColor: "rgba(255,0,0,0.8)" },
                      }}
                      onClick={() => {
                        setRemovedImages((prev) => [
                          ...prev,
                          pageData.attachments[idx],
                        ]);
                        pageData.attachments = pageData.attachments.filter(
                          (_, i) => i !== idx
                        );
                      }}
                    >
                      <CloseIcon fontSize="small" />{" "}
                    </IconButton>{" "}
                  </Box>
                ))}{" "}
              </Box>{" "}
            </Box>
          )}{" "}
          {pageData?.status !== "completed" && (
            <Box sx={{ marginTop: "10px" }}>
              {" "}
              {pageData?.status === "in review" &&
                currentUser?._id !== pageData?.submittedBy && (
                  <>
                    {" "}
                    <Button onClick={handleAccept} variant="contained">
                      Accept{" "}
                    </Button>{" "}
                    <Button onClick={handleEdit} variant="contained">
                      Edit{" "}
                    </Button>{" "}
                  </>
                )}{" "}
              {(pageData?.status === "in progress" ||
                pageData?.status === "not started") && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                  <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={!isSaveEnabled()}
                    sx={{
                      fontWeight: 600,
                      textTransform: "none",
                      borderRadius: 2,
                    }}
                  >
                    Save{" "}
                  </Button>{" "}
                  <Button
                    onClick={handleUndo}
                    variant="outlined"
                    color="secondary"
                    disabled={history.length <= 1}
                    sx={{
                      fontWeight: 600,
                      textTransform: "none",
                      borderRadius: 2,
                    }}
                  >
                    Undo{" "}
                  </Button>{" "}
                  <Button
                    onClick={handleRedo}
                    variant="outlined"
                    color="secondary"
                    disabled={redoStack.length === 0}
                    sx={{
                      fontWeight: 600,
                      textTransform: "none",
                      borderRadius: 2,
                    }}
                  >
                    Redo{" "}
                  </Button>{" "}
                  <Button
                    onClick={handleSubmitForReview}
                    variant="contained"
                    color="success"
                    disabled={!isSubmitForReviewEnabled()}
                    sx={{
                      fontWeight: 600,
                      textTransform: "none",
                      borderRadius: 2,
                    }}
                  >
                    Submit for Review{" "}
                  </Button>{" "}
                </Box>
              )}{" "}
            </Box>
          )}
          {currentUser?.role === "admin" && (
            <Box
              sx={{
                marginTop: "16px",
                p: 1.5,
                borderRadius: 2,
                border: "1px dashed #bdbdbd",
                backgroundColor: "#fafafa",
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Admin actions
              </Typography>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                {pageData?.status === "completed" && (
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => {
                      setAdminActionType("reopen");
                      setAdminActionDialogOpen(true);
                    }}
                    sx={{
                      fontWeight: 600,
                      textTransform: "none",
                      borderRadius: 2,
                    }}
                  >
                    Move to In Progress
                  </Button>
                )}

                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setAdminActionType("reset");
                    setAdminActionDialogOpen(true);
                  }}
                  sx={{
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: 2,
                  }}
                >
                  Reset to Not Started
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      {/* Campaign Tips Carousel */}
      <Dialog
        open={tipsOpen}
        onClose={() => setTipsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <TipsAndUpdatesIcon color="primary" />
            <Typography variant="h6"> Tips</Typography>
          </Box>

          <IconButton onClick={() => setTipsOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            {CAMPAIGN_TIPS[activeTipIndex].title}
          </Typography>

          <Typography
            variant="body2"
            sx={{ whiteSpace: "pre-line", lineHeight: 1.7 }}
          >
            {CAMPAIGN_TIPS[activeTipIndex].body}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              size="small"
              onClick={() => setActiveTipIndex((i) => Math.max(0, i - 1))}
              disabled={activeTipIndex === 0}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() =>
                setActiveTipIndex((i) =>
                  Math.min(CAMPAIGN_TIPS.length - 1, i + 1)
                )
              }
              disabled={activeTipIndex === CAMPAIGN_TIPS.length - 1}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption">
              Tip {activeTipIndex + 1} of {CAMPAIGN_TIPS.length}
            </Typography>
          </Box>
          <Button onClick={() => setTipsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* How-To Guide with topic list + hamburger */}
      <Dialog
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              size="small"
              onClick={() => {
                if (isMobile) {
                  setShowTopicList((prev) => !prev);
                }
              }}
              sx={{ mr: 1 }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6"> How-To Guide </Typography>
          </Box>
          <IconButton onClick={() => setGuideOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile && showTopicList ? "column" : "row",
              minHeight: 320,
            }}
          >
            {(showTopicList || !isMobile) && (
              <Box
                sx={{
                  width: { xs: "100%", sm: 260 },
                  borderRight: !isMobile
                    ? "1px solid rgba(0,0,0,0.12)"
                    : "none",
                  borderBottom:
                    isMobile && showTopicList
                      ? "1px solid rgba(0,0,0,0.12)"
                      : "none",
                }}
              >
                <List dense>
                  {HOW_TO_TOPICS.map((topic) => (
                    <ListItemButton
                      key={topic.id}
                      selected={topic.id === activeTopicId}
                      onClick={() => {
                        setActiveTopicId(topic.id);
                        if (isMobile) {
                          setShowTopicList(false);
                        }
                      }}
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: "action.selected",
                        },
                      }}
                    >
                      <ListItemText
                        primary={topic.title}
                        primaryTypographyProps={{ fontWeight: 500 }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            )}
            {(!showTopicList || !isMobile) && (
              <Box sx={{ flex: 1, p: 2.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <HelpOutlineIcon color="primary" fontSize="small" />
                  <Typography variant="h6">{activeTopic.title}</Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-line", lineHeight: 1.8, mb: 2 }}
                >
                  {activeTopic.body}
                </Typography>
                {activeTopicId === "getting-started" && (
                  <Box
                    sx={{
                      mb: 2,
                      position: "relative",
                      paddingTop: "56.25%",
                      borderRadius: 2,
                      overflow: "hidden",
                      boxShadow: 1,
                    }}
                  >
                    <iframe
                      src="https://www.youtube.com/embed/LIeGezuncbo"
                      title="Getting Started Walkthrough"
                      frameBorder="0"
                      allowFullScreen
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                      }}
                    />
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary">
                  For more detailed project-specific instructions, you may also
                  contact{" "}
                  <MuiLink
                    href="mailto:dh@usm.edu"
                    sx={{
                      color: "primary.dark",
                      fontWeight: 600,
                      textDecorationColor: "primary.light",
                      "&:hover": {
                        color: "primary.main",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    dh@usm.edu
                  </MuiLink>{" "}
                  or visit the{" "}
                  <MuiLink
                    href="/Chat-Thread"
                    sx={{
                      color: "secondary.dark",
                      fontWeight: 600,
                      textDecorationColor: "secondary.light",
                      "&:hover": {
                        color: "secondary.main",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Hub discussion forum
                  </MuiLink>
                  .
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGuideOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialogAction === "save" &&
              "Are you sure you want to save the transcription?"}
            {dialogAction === "submit" &&
              "Are you sure you want to submit this page for review?"}
            {dialogAction === "accept" &&
              "Are you sure you want to mark this page as completed?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          {dialogAction === "save" && (
            <Button onClick={confirmSave} color="primary">
              Confirm
            </Button>
          )}
          {dialogAction === "submit" && (
            <Button onClick={confirmSubmitForReview} color="primary">
              Confirm
            </Button>
          )}
          {dialogAction === "accept" && (
            <Button onClick={confirmAccept} color="primary">
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={successDialog} onClose={handleSuccessClose}>
        <DialogTitle> Success</DialogTitle>
        <DialogContent>
          <DialogContentText>{successMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessClose} color="primary">
            {" "}
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={warningDialogOpen}
        onClose={() => setWarningDialogOpen(false)}
      >
        <DialogTitle>Please take another look</DialogTitle>
        <DialogContent>
          <DialogContentText>{warningDialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarningDialogOpen(false)} color="primary">
            Go Back
          </Button>

          {warningDialogMode === "submit" && (
            <Button
              onClick={continueSubmitAnyway}
              color="warning"
              variant="contained"
            >
              Submit Anyway
            </Button>
          )}

          {warningDialogMode === "accept" && (
            <Button
              onClick={continueAcceptAnyway}
              color="warning"
              variant="contained"
            >
              Mark Complete Anyway
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog
        open={adminActionDialogOpen}
        onClose={() => setAdminActionDialogOpen(false)}
      >
        <DialogTitle>Confirm Admin Action</DialogTitle>{" "}
        <DialogContent>
          {" "}
          <DialogContentText>
            {" "}
            {adminActionType === "reopen" &&
              "Are you sure you want to move this completed page back to in progress?"}{" "}
            {adminActionType === "reset" &&
              "Are you sure you want to reset this page to not started? This will clear page work and related event tracking for this page."}{" "}
          </DialogContentText>{" "}
        </DialogContent>{" "}
        <DialogActions>
          {" "}
          <Button onClick={() => setAdminActionDialogOpen(false)}>
            Cancel
          </Button>{" "}
          {adminActionType === "reopen" && (
            <Button
              color="warning"
              variant="contained"
              onClick={async () => {
                setAdminActionDialogOpen(false);
                await handleAdminMoveToInProgress();
              }}
            >
              Confirm{" "}
            </Button>
          )}{" "}
          {adminActionType === "reset" && (
            <Button
              color="error"
              variant="contained"
              onClick={async () => {
                setAdminActionDialogOpen(false);
                await handleAdminResetToNotStarted();
              }}
            >
              Confirm{" "}
            </Button>
          )}{" "}
        </DialogActions>
      </Dialog>
      <Dialog open={reviewIdleDialogOpen} disableEscapeKeyDown>
        <DialogTitle>Are you still reviewing?</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Your review timer has been paused because there hasn't been any
            activity for a while. If you're still reviewing this page, you can
            continue where you left off.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleStayPaused} color="inherit">
            Stay Paused
          </Button>

          <Button
            onClick={handleContinueReviewing}
            variant="contained"
            autoFocus
          >
            Continue Reviewing
          </Button>
        </DialogActions>
      </Dialog>
      {isLockedByAnotherUser && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(255, 255, 255, 0.62)",
            backdropFilter: "blur(2px)",
            zIndex: 30,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            px: 2,
          }}
        >
          <Box
            sx={{
              maxWidth: 560,
              width: "100%",
              backgroundColor: "rgba(255,255,255,0.96)",
              border: "1px solid #e5e7eb",
              borderRadius: 3,
              boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
              p: 3,
              textAlign: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#111111", mb: 1 }}
            >
              This page is currently being worked on by another volunteer.
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: "#374151", mb: 3, lineHeight: 1.7 }}
            >
              Please move to another page for now.
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={goToPrevPage}
                disabled={selectedPage === 1}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#111111",
                  borderColor: "#d1d5db",
                  backgroundColor: "#ffffff",
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "#f9fafb",
                    borderColor: "#9ca3af",
                  },
                }}
              >
                Previous Page
              </Button>

              <Button
                variant="contained"
                onClick={handleBackToAllPages}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#111111",
                  backgroundColor: "#facc15",
                  border: "1px solid #eab308",
                  borderRadius: 2,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#eab308",
                    boxShadow: "none",
                  },
                }}
              >
                View All Pages
              </Button>

              <Button
                variant="outlined"
                endIcon={<ArrowForward />}
                onClick={goToNextPage}
                disabled={selectedPage === totalPages.length}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#111111",
                  borderColor: "#d1d5db",
                  backgroundColor: "#ffffff",
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "#f9fafb",
                    borderColor: "#9ca3af",
                  },
                }}
              >
                Next Page
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PageEditor;
